import { Box, Typography } from '@mui/material'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import {
  BarChart,
  DatePicker,
  MButton,
  SvgImage,
  YearDropDown,
  YearMonthPicker
} from 'components'
import { MONTHS, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { isEmpty, upperFirst } from 'lodash'
import { toTitleCase, useLocalStorage } from 'utils'
import { exportExcel } from 'utils/helpers/exportExcel'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import {
  getMetricUnit,
  getSeriesDataLength,
  getUsagePayload,
  getXAxisData,
  valueFormatter
} from './utils'

interface IUsageViewGraph {
  serviceType: string
  viewType: 'monthly' | 'daily' | 'hourly'
  filters: Record<string, Record<string, string>>
  setFilters: Dispatch<SetStateAction<Record<string, Record<string, string>>>>
}

export const UsageViewGraph = ({
  serviceType,
  viewType,
  filters,
  setFilters
}: IUsageViewGraph): JSX.Element => {
  const { currentAccount, isAdmin, getUsage, premiseId, userEmail, usage } =
    useAuth()
  const [authToken] = useLocalStorage(USER_TOKEN, '')

  const [seriesData, setSeriesData] = useState<number[]>([])
  const [xAxisData, setXAxisData] = useState<string[]>(MONTHS)
  const [loading, setLoading] = useState<boolean>(false)
  const hasSeriesData = !(
    !seriesData?.some((el) => el !== null) || isEmpty(seriesData)
  )

  const isMobile = useMobileBreakpoints()

  const handleDateFilterChange = (event: any): void => {
    setFilters({
      ...filters,
      [serviceType]: {
        ...filters[serviceType],
        [viewType]: getViewTypeFilterValue(viewType, event)
      }
    })
  }

  const initUsageData = async (): Promise<void> => {
    setLoading(true)
    const payload = getUsagePayload(filters, serviceType, viewType)
    if (!isEmpty(currentAccount?.premiseId ?? premiseId)) {
      await getUsage({
        AccessToken: authToken,
        premiseId: currentAccount?.premiseId ?? premiseId,
        admin: isAdmin,
        email: userEmail,
        ...payload
      })
    }
    setLoading(false)
  }
  // Set X Axis Data
  useEffect(() => {
    const xAxisData = getXAxisData(filters, serviceType, viewType)
    setXAxisData(xAxisData)
  }, [filters, serviceType, viewType])

  // Initialize Usage Data
  useEffect(() => {
    void initUsageData()
  }, [filters, serviceType, viewType, currentAccount])

  // Map Usage Data as Series Data
  useEffect(() => {
    const seriesDataLength = getSeriesDataLength(viewType, xAxisData)
    const seriesData: any = Array.from(
      { length: seriesDataLength },
      () => null
    )
    setSeriesData([])

    usage?.usage?.usageHistory?.forEach((usageH: any) => {
      usageH.usageData.forEach((item: any) => {
        const period = item.period.slice(-2)

        if (viewType === 'hourly') {
          seriesData[parseInt(period)] = Math.round(item.value)
          return
        }

        seriesData[parseInt(period) - 1] = Math.round(item.value)
      })
    })

    setSeriesData(seriesData)
  }, [usage?.usage?.usageHistory, viewType, xAxisData])


  const handleExportExcel = (): void => {
    const excelData = [
      ['Period', 'Value'],
      ...(xAxisData?.map((period, index) => [period, seriesData?.[index]?.toString() ?? '']) ?? [])
    ]

    exportExcel(excelData, 'Usage History', `${toTitleCase(viewType)} ${toTitleCase(serviceType)} Data - ${filters[serviceType][viewType]}.xlsx`)
  }


  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={1}
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography fontSize={24} color={color.grey[900]} fontWeight={500}>
            {`${upperFirst(viewType)} Billed Usage History`}
          </Typography>

          <Typography fontSize={16} color={color.grey[700]} fontWeight={400}>
            {`${getMetricUnit(serviceType)}`}
          </Typography>
        </Box>

        <Box>
          {renderDateFilter(
            viewType,
            filters[serviceType][viewType],
            handleDateFilterChange
          )}
        </Box>
      </Box>

      <BarChart
        seriesData={seriesData}
        xAxisData={xAxisData}
        valueFormatter={(value) => valueFormatter(value, serviceType)}
        loading={loading}
      />

      <Box
        display="flex"
        alignItems={isMobile ? 'flex-start' : 'center'}
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent={isMobile ? 'flex-start' : 'space-between'}
        gap={2}
      >
        <MButton
          variant="outlined"
          texttransform="none"
          rounded="true"
          size="large"
          startIcon={<SvgImage name="BillDownloadPdf" width={25} height={25} />}
          fullWidth={isMobile}
          disabled={loading || !hasSeriesData}
          onClick={handleExportExcel}
        >
          Export to excel
        </MButton>

        <Typography
          fontSize={12}
          sx={{
            flex: 1,
            textAlign: isMobile ? 'left' : 'right'
          }}
        >
          *Usage is shown in the month it is billed. For example, February usage reflects the service period from January to February that appears on your February bill.
          <br />
          Usage is grouped by billing month. Each bar reflects the service period covered by that month’s bill.
        </Typography>
      </Box>
    </Box>
  )
}

const renderDateFilter = (
  viewType: 'monthly' | 'daily' | 'hourly',
  value: string,
  onChange: any,
  options?: string[]
): JSX.Element => {
  const defaultYearOptions = [`${dayjs().year()}`, `${dayjs().subtract(1, 'year').year()}`]

  switch (viewType) {
    case 'monthly':
      return (
        <YearDropDown
          value={value}
          onChange={onChange}
          yearOptions={options ?? defaultYearOptions}
        />
      )
    case 'daily':
      return <YearMonthPicker value={value} onChange={onChange} />
    case 'hourly':
      return <DatePicker value={value} onChange={onChange} />
  }
}

const getViewTypeFilterValue = (
  viewType: 'monthly' | 'daily' | 'hourly',
  event: any
): string => {
  switch (viewType) {
    case 'monthly':
      return event.target.value
    case 'daily':
      return event.format('YYYY-MM')
    case 'hourly':
      return event.format('YYYY-MM-DD')
  }
}
